import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

export type ValueArtId =
  | 'innovation'
  | 'excellence'
  | 'collaboration'
  | 'integrity'
  | 'customer'
  | 'agility'
  | 'accountability'
  | 'improvement';

/**
 * The animated scene inside a value card.
 *
 * These are SVG rather than bitmaps on purpose: the brief calls for the
 * artwork itself to animate — bars growing, a line drawing itself, leaves
 * swaying, particles orbiting. A raster image can only be zoomed or faded
 * as a whole, so none of that is reachable with a .webp.
 *
 * Everything animates via transform/opacity/filter only. Motion lives in
 * the stylesheet so `prefers-reduced-motion` can switch all of it off in
 * one place.
 */
@Component({
  selector: 'app-value-art',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./value-art.component.scss'],
  template: `
    <svg
      class="art"
      viewBox="0 0 320 190"
      fill="none"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      [ngSwitch]="id"
    >
      <defs>
        <linearGradient [attr.id]="'vg-' + id" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#FF3D9A" />
          <stop offset="52%" stop-color="#8A5CFF" />
          <stop offset="100%" stop-color="#3B82F6" />
        </linearGradient>
        <radialGradient [attr.id]="'vr-' + id" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="#8A5CFF" stop-opacity=".55" />
          <stop offset="100%" stop-color="#8A5CFF" stop-opacity="0" />
        </radialGradient>
      </defs>

      <!-- soft key light, shared by every scene -->
      <ellipse
        class="art__key"
        cx="160"
        cy="100"
        rx="104"
        ry="74"
        [attr.fill]="'url(#vr-' + id + ')'"
      />

      <!-- ============ INNOVATION — bulb, filament, orbiting sparks ============ -->
      <g *ngSwitchCase="'innovation'" [attr.stroke]="'url(#vg-' + id + ')'">
        <g class="art__bulb" fill="none" stroke-width="2" stroke-linecap="round">
          <path d="M160 46a38 38 0 0 0-22 69v14h44v-14a38 38 0 0 0-22-69z" />
          <path d="M143 133h34M147 143h26" stroke-width="2.4" />
        </g>
        <path
          class="art__filament"
          d="M148 104c4-10 8 6 12-4s8 6 12-4"
          fill="none"
          stroke-width="2.4"
          stroke-linecap="round"
        />
        <circle class="art__halo" cx="160" cy="92" r="30" fill="none" stroke-width="1" opacity=".4" />
        <g class="art__orbit" fill="#FF3D9A">
          <circle class="art__spark art__spark--1" cx="160" cy="92" r="2.6" />
          <circle class="art__spark art__spark--2" cx="160" cy="92" r="2" />
          <circle class="art__spark art__spark--3" cx="160" cy="92" r="2.3" />
        </g>
      </g>

      <!-- ============ EXCELLENCE — star, pedestal, light sweep ============ -->
      <g *ngSwitchCase="'excellence'">
        <g class="art__star" [attr.stroke]="'url(#vg-' + id + ')'" fill="none" stroke-width="2" stroke-linejoin="round">
          <path d="m160 42 17 35 38 5-27 27 6 38-34-18-34 18 6-38-27-27 38-5z" />
          <path d="m160 66 9 19 20 3-14 14 3 20-18-10-18 10 3-20-14-14 20-3z" opacity=".45" />
        </g>
        <g [attr.stroke]="'url(#vg-' + id + ')'" fill="none" stroke-width="2" stroke-linecap="round">
          <path d="M160 149v-12M132 163h56M138 163l4-14h36l4 14" />
        </g>
        <rect class="art__sweep" x="96" y="34" width="26" height="140" fill="#fff" opacity=".12" />
      </g>

      <!-- ============ COLLABORATION — linked nodes, pulsing bonds ============ -->
      <g *ngSwitchCase="'collaboration'">
        <g class="art__links" [attr.stroke]="'url(#vg-' + id + ')'" stroke-width="1.6" fill="none">
          <path d="M112 74 160 58 208 74 190 122 130 122z" />
          <path d="M160 58v40M112 74l48 24M208 74l-48 24M130 122l30-24M190 122l-30-24" opacity=".55" />
        </g>
        <g class="art__nodes" [attr.fill]="'url(#vg-' + id + ')'">
          <circle class="art__node art__node--1" cx="160" cy="58" r="9" />
          <circle class="art__node art__node--2" cx="112" cy="74" r="8" />
          <circle class="art__node art__node--3" cx="208" cy="74" r="8" />
          <circle class="art__node art__node--4" cx="130" cy="122" r="7" />
          <circle class="art__node art__node--5" cx="190" cy="122" r="7" />
        </g>
        <circle class="art__core" cx="160" cy="98" r="13" [attr.stroke]="'url(#vg-' + id + ')'" stroke-width="2" fill="none" />
      </g>

      <!-- ============ INTEGRITY — balance scales, gentle tilt ============ -->
      <g *ngSwitchCase="'integrity'" [attr.stroke]="'url(#vg-' + id + ')'" fill="none" stroke-width="2" stroke-linecap="round">
        <path d="M160 52v96M136 156h48" />
        <path d="M148 148h24" opacity=".6" />
        <g class="art__beam">
          <path d="M104 70h112" stroke-width="2.4" />
          <path d="M104 70 86 104h36zM216 70l18 34h-36z" stroke-linejoin="round" />
          <path d="M86 104a18 18 0 0 0 36 0M198 104a18 18 0 0 0 36 0" opacity=".7" />
        </g>
        <circle cx="160" cy="52" r="6" [attr.fill]="'url(#vg-' + id + ')'" stroke="none" />
      </g>

      <!-- ============ CUSTOMER-CENTRICITY — faces, centre pulses ============ -->
      <g *ngSwitchCase="'customer'" fill="none" stroke-width="2">
        <g class="art__face art__face--side" [attr.stroke]="'url(#vg-' + id + ')'" opacity=".4">
          <circle cx="86" cy="98" r="24" />
          <path d="M77 92h.01M95 92h.01M77 108a12 12 0 0 1 18 0" stroke-linecap="round" />
        </g>
        <g class="art__face art__face--side" [attr.stroke]="'url(#vg-' + id + ')'" opacity=".4">
          <circle cx="234" cy="98" r="24" />
          <path d="M225 92h.01M243 92h.01M225 106h18" stroke-linecap="round" />
        </g>
        <g class="art__face art__face--main" [attr.stroke]="'url(#vg-' + id + ')'" stroke-width="2.4">
          <circle cx="160" cy="98" r="34" />
          <path d="M147 88h.01M173 88h.01" stroke-linecap="round" stroke-width="4" />
          <path d="M143 106a20 20 0 0 0 34 0" stroke-linecap="round" />
        </g>
      </g>

      <!-- ============ AGILITY — chevron with motion trail ============ -->
      <g *ngSwitchCase="'agility'">
        <g class="art__trail" [attr.stroke]="'url(#vg-' + id + ')'" stroke-width="2" stroke-linecap="round">
          <path class="art__streak art__streak--1" d="M52 76h58" opacity=".5" />
          <path class="art__streak art__streak--2" d="M40 98h74" opacity=".65" />
          <path class="art__streak art__streak--3" d="M58 120h52" opacity=".4" />
        </g>
        <g class="art__dart" [attr.stroke]="'url(#vg-' + id + ')'" fill="none" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="m146 62 46 36-46 36" />
          <path d="m190 62 46 36-46 36" opacity=".55" />
        </g>
        <g class="art__motes" fill="#FF3D9A">
          <circle class="art__mote art__mote--1" cx="70" cy="70" r="2.2" />
          <circle class="art__mote art__mote--2" cx="70" cy="104" r="1.8" />
          <circle class="art__mote art__mote--3" cx="70" cy="128" r="2" />
        </g>
      </g>

      <!-- ============ ACCOUNTABILITY — shield, sprout, swaying leaves ============ -->
      <g *ngSwitchCase="'accountability'" fill="none" stroke-width="2" stroke-linecap="round">
        <path
          [attr.stroke]="'url(#vg-' + id + ')'"
          d="M160 40 108 60v40c0 32 22 58 52 66 30-8 52-34 52-66V60z"
          stroke-linejoin="round"
        />
        <path d="M160 148v-40" [attr.stroke]="'url(#vg-' + id + ')'" stroke-width="2.4" />
        <path
          class="art__leaf art__leaf--l"
          d="M160 118c-14 0-24-8-26-22 15-3 26 6 26 22z"
          [attr.stroke]="'url(#vg-' + id + ')'"
          stroke-linejoin="round"
        />
        <path
          class="art__leaf art__leaf--r"
          d="M160 108c14 0 24-8 26-22-15-3-26 6-26 22z"
          [attr.stroke]="'url(#vg-' + id + ')'"
          stroke-linejoin="round"
        />
        <circle class="art__seed" cx="160" cy="74" r="5" [attr.fill]="'url(#vg-' + id + ')'" stroke="none" />
      </g>

      <!-- ============ IMPROVEMENT — bars grow, line draws ============ -->
      <g *ngSwitchCase="'improvement'">
        <g class="art__bars" [attr.fill]="'url(#vg-' + id + ')'">
          <rect class="art__bar art__bar--1" x="82" y="112" width="18" height="38" rx="4" />
          <rect class="art__bar art__bar--2" x="112" y="96" width="18" height="54" rx="4" />
          <rect class="art__bar art__bar--3" x="142" y="104" width="18" height="46" rx="4" />
          <rect class="art__bar art__bar--4" x="172" y="76" width="18" height="74" rx="4" />
          <rect class="art__bar art__bar--5" x="202" y="60" width="18" height="90" rx="4" />
        </g>
        <path
          class="art__curve"
          d="M82 116 121 98 151 106 181 74 211 58 240 44"
          [attr.stroke]="'url(#vg-' + id + ')'"
          stroke-width="2.6"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path
          class="art__tip"
          d="m228 44 14-2-4 14"
          [attr.stroke]="'url(#vg-' + id + ')'"
          stroke-width="2.6"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
        <path d="M74 150h176" stroke="rgba(255,255,255,.16)" stroke-width="1.4" />
      </g>
    </svg>
  `,
})
export class ValueArtComponent {
  @Input({ required: true }) id!: ValueArtId;
}
